import React, { useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";

import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../components/Auth/AuthLayout";
import {
  EyeSlashIcon,
  LoginEyeIcon,
  LoginIcon,
} from "../../components/Auth/AuthIcons";
import { useAuth } from "../../contexts/AuthContext";
import { signInSchema } from "../../schemas/Auth.schema";
import { useFormik } from "formik";
import Loader from "../../components/Molecules/Loader/Loader";
import { useToast } from "../../services/utils";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { buildSignupPath } from "../../utils/signupRedirect";

const SignIn = () => {
  const { showToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const { signin } = useAuth();

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      showPassword: false,
    },
    validationSchema: signInSchema, // Corrected the property name
    onSubmit: async (values) => {
      if (isSubmitting) return; // Prevent multiple submissions

      setIsSubmitting(true);

      const { showPassword, ...payload } = values;
      try {
        setIsLoading(true);
        const response: any = await signin(payload);
        if (response?.access_token) {
          // token and permissions are handled by AuthContext
          // Let the root route handle the redirect based on permissions
          // This prevents race condition where user is redirected before permissions are loaded
          navigate("/");
          showToast(t("auth.signIn.toast.success"), "success");
        }
      } catch (err: any) {
        showToast(err?.response?.data?.message, "error");
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // const handleSignIn = async (e: any) => {
  //   e.preventDefault();
  //   try {
  //     const response: any = await signin(formData);
  //     if (response?.access_token) {
  //       navigate("/domain");
  //       showToast("Logged in successfully", "success");
  //     }
  //   } catch (err: any) {
  //     showToast(err?.response?.data?.message, "error");
  //   }
  // };

  // const handleChange = (e: any) => {
  //   setFormData({ ...formData, [e.target.name]: e.target.value });
  // };
  return (
    <AuthLayout>
      <Loader isLoading={isLoading} />
      <Typography component="h1" className="auth-form-title">
        {t("auth.signIn.header")}
      </Typography>
      <Typography className="auth-form-subtitle">
        {t("auth.signIn.subtitle")}
      </Typography>

      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputLabel className="label">
              {t("auth.signIn.labels.username")}
              <span className="required-asterisk">*</span>
            </InputLabel>
            <TextField
              autoComplete="username"
              name="username"
              variant="outlined"
              required
              fullWidth
              id="username"
              autoFocus
              placeholder={t("auth.signIn.placeholders.username")}
              className="form-control"
              onChange={formik.handleChange}
              value={formik.values.username}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel className="label withLink">
              <span>
                {t("auth.signIn.labels.password")}
                <span className="required-asterisk">*</span>
              </span>
              <Link to="/forgotpassword">
                {t("auth.signIn.forgotPassword")}
              </Link>
            </InputLabel>
            <TextField
              required
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="new-password"
              placeholder={t("auth.signIn.placeholders.password")}
              className="form-control withAdornment password-box"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <LoginEyeIcon /> : <EyeSlashIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className="btn-orange btn-dark"
          startIcon={<LoginIcon />}
        >
          {t("auth.signIn.button")}
        </Button>

        <Grid item xs={12} className="regular-text">
          {t("auth.signIn.noAccount")}
          <Link to={buildSignupPath()}>{t("auth.signIn.signUp")}</Link>
        </Grid>
      </form>
    </AuthLayout>
  );
};

export default SignIn;
